/**
 * CheckSummary - Final summary of tech check results
 * Nordic Sanctuary design: Clear results, helpful guidance
 */

import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@jourfx/ui";

interface CheckResult {
  id: "camera" | "microphone" | "speaker" | "network";
  name: string;
  status: "pass" | "warning" | "fail" | "pending" | "running";
  required: boolean;
}

interface CheckSummaryProps {
  results: CheckResult[];
  onContinue: () => void;
  onRetry: () => void;
}

export function CheckSummary({ results, onContinue, onRetry }: CheckSummaryProps) {
  const { t } = useTranslation("techcheck");
  const failedChecks = results.filter((r) => r.status === "fail");
  const hasFailures = failedChecks.length > 0;
  const hasWarnings = results.some((r) => r.status === "warning");
  const allPassed = results.every((r) => r.status === "pass");

  const pendingConfig = {
    icon: AlertTriangle,
    bgClass: "bg-[var(--stone-50)]",
    borderClass: "border-[var(--stone-200)]",
    iconClass: "text-[var(--stone-400)]",
    textClass: "text-[var(--stone-500)]",
  };

  const statusConfig: Record<string, typeof pendingConfig> = {
    pass: {
      icon: CheckCircle,
      bgClass: "bg-[var(--sage-50)]",
      borderClass: "border-[var(--sage-200)]",
      iconClass: "text-[var(--sage-600)]",
      textClass: "text-[var(--sage-700)]",
    },
    warning: {
      icon: AlertTriangle,
      bgClass: "bg-[oklch(0.97_0.04_80)]",
      borderClass: "border-[oklch(0.88_0.08_80)]",
      iconClass: "text-[oklch(0.55_0.15_80)]",
      textClass: "text-[oklch(0.45_0.12_80)]",
    },
    fail: {
      icon: XCircle,
      bgClass: "bg-[oklch(0.97_0.02_25)]",
      borderClass: "border-[oklch(0.90_0.04_25)]",
      iconClass: "text-[oklch(0.55_0.18_25)]",
      textClass: "text-[oklch(0.45_0.12_25)]",
    },
  };

  return (
    <div className="space-y-6">
      {/* Summary header */}
      <div className="text-center">
        <div
          className={`
            w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center
            ${
              allPassed
                ? "bg-[var(--sage-500)]"
                : hasFailures
                  ? "bg-[oklch(0.55_0.18_25)]"
                  : "bg-[oklch(0.60_0.15_80)]"
            }
          `}
        >
          {allPassed ? (
            <CheckCircle className="w-8 h-8 text-white" aria-hidden="true" />
          ) : hasFailures ? (
            <XCircle className="w-8 h-8 text-white" aria-hidden="true" />
          ) : (
            <AlertTriangle className="w-8 h-8 text-white" aria-hidden="true" />
          )}
        </div>
        <h2 className="text-xl font-bold text-[var(--stone-800)]">
          {allPassed
            ? t("summary.allPassed")
            : hasFailures
              ? t("summary.failed")
              : t("summary.someIssues")}
        </h2>
        <p className="text-[var(--stone-500)] mt-1" aria-live="polite">
          {allPassed
            ? t("summary.allPassedMessage")
            : hasFailures
              ? t("summary.failedMessage")
              : t("summary.someIssuesMessage")}
        </p>
      </div>

      {/* Results list */}
      <div className="space-y-2">
        {results.map((result) => {
          const config = statusConfig[result.status] ?? pendingConfig;
          const Icon = config.icon;

          return (
            <div
              key={result.name}
              className={`flex items-center justify-between p-4 rounded-xl border ${config.bgClass} ${config.borderClass}`}
            >
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${config.iconClass}`} aria-hidden="true" />
                <span className={`font-medium ${config.textClass}`}>{result.name}</span>
              </div>
              {result.status === "fail" && (
                <span className="text-xs font-medium text-[oklch(0.55_0.15_25)] bg-[oklch(0.92_0.04_25)] px-2 py-0.5 rounded">
                  {t("summary.needsAttention")}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {hasFailures && (
        <div className="p-4 rounded-xl bg-[oklch(0.97_0.02_25)] border border-[oklch(0.90_0.04_25)] space-y-3">
          <p className="text-[oklch(0.40_0.10_25)] font-medium">
            {t("summary.failedTroubleshootingTitle")}
          </p>
          <ul className="space-y-2 text-sm text-[oklch(0.50_0.08_25)]">
            {failedChecks.some((item) => item.id === "camera") && (
              <li>{t("troubleshooting.camera")}</li>
            )}
            {failedChecks.some((item) => item.id === "microphone") && (
              <li>{t("troubleshooting.microphone")}</li>
            )}
            {failedChecks.some((item) => item.id === "network") && (
              <li>{t("troubleshooting.network")}</li>
            )}
            {failedChecks.some((item) => item.id === "speaker") && (
              <li>{t("troubleshooting.speaker")}</li>
            )}
          </ul>
          <p className="text-sm text-[oklch(0.50_0.08_25)]">
            {t("summary.continueDespiteIssues")}
          </p>
        </div>
      )}

      {/* Warning message */}
      {!hasFailures && hasWarnings && (
        <div className="p-4 rounded-xl bg-[oklch(0.97_0.04_80)] border border-[oklch(0.88_0.08_80)]">
          <p className="text-[oklch(0.40_0.10_80)]">
            {t("summary.warningNote")}
          </p>
        </div>
      )}

      {/* Action */}
      <Button
        variant="default"
        size="client"
        className="w-full"
        onClick={onContinue}
      >
        {t("summary.continue")}
        <ArrowRight aria-hidden="true" />
      </Button>

      {(hasFailures || hasWarnings) && (
        <Button variant="secondary" size="client" className="w-full" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          {t("summary.retry")}
        </Button>
      )}
    </div>
  );
}
